import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, ListGroup, Spinner, Badge } from 'react-bootstrap';
import { Database, FileText, PencilSquare, Plus, Eye, Calendar } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import config from '../config';

const AdminContentPage = () => {
  const [contentData, setContentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContentData();
  }, []);

  const fetchContentData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${config.api.url}admin/stats?format=json`);
      
      if (response.ok) {
        const data = await response.json();
        setContentData(data);
      } else {
        throw new Error(`Failed to fetch content data: ${response.status}`);
      }
    } catch (err) {
      console.error('Error fetching content data:', err);
      setError(`Failed to load content data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getPageDisplayName = (pageId) => {
    const pageNames = {
      'home': 'Homepage',
      'about': 'About',
      'sponsors': 'Sponsors',
      'timeline': 'Timeline',
      'tracks': 'Tracks Overview',
      'tracks/smart-charging': 'Smart Charging',
      'tracks/tailings-treatment': 'Tailings Treatment', 
      'tracks/bitumen-viscosity': 'Bitumen Viscosity',
      'tracks/answers': 'FAQ/Answers'
    };
    return pageNames[pageId] || pageId.charAt(0).toUpperCase() + pageId.slice(1);
  };

  const getPageDescription = (pageId) => {
    const descriptions = {
      'home': 'Main landing page content',
      'about': 'About the challenge',
      'sponsors': 'Sponsor information and logos',
      'timeline': 'Challenge timeline and milestones',
      'tracks': 'Overview of all tracks',
      'tracks/smart-charging': 'Smart charging track details',
      'tracks/tailings-treatment': 'Tailings treatment track details',
      'tracks/bitumen-viscosity': 'Bitumen viscosity track details',
      'tracks/answers': 'Frequently asked questions'
    };
    return descriptions[pageId] || 'Content page';
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading content data...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Content</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchContentData}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  const contentStats = contentData?.content || {};
  const pagesByContent = contentStats.by_page || {};
  const allPages = Object.keys(pagesByContent);

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <Database size={32} className="me-3 text-primary" />
              <div>
                <h1 className="mb-0">Content Management</h1>
                <p className="text-muted mb-0">
                  Manage {contentStats.total_pages || 0} pages with {contentStats.total_blocks || 0} content sections
                </p>
              </div>
            </div>
            <Button as={Link} to="/admin" variant="outline-secondary">
              Back to Dashboard
            </Button>
          </div>
        </Col>
      </Row>

      {/* Content Statistics */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary mb-1">{contentStats.total_pages || 0}</h3>
              <p className="text-muted mb-0">Total Pages</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success mb-1">{contentStats.total_blocks || 0}</h3>
              <p className="text-muted mb-0">Content Sections</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-info mb-1">
                {contentStats.total_blocks && contentStats.total_pages 
                  ? Math.round(contentStats.total_blocks / contentStats.total_pages * 10) / 10 
                  : 0}
              </h3>
              <p className="text-muted mb-0">Avg Sections/Page</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Website Pages</h5>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" onClick={fetchContentData}>
                    <Calendar className="me-1" />
                    Refresh
                  </Button>
                  <Button 
                    as={Link} 
                    to="/page/new" 
                    variant="success" 
                    size="sm"
                  >
                    <Plus className="me-1" />
                    New Page
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {allPages.length === 0 ? (
                <div className="text-center py-5">
                  <FileText size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">No Content Pages Found</h5>
                  <p className="text-muted">
                    Create your first page to get started with content management.
                  </p>
                  <Button as={Link} to="/page/home" variant="primary">
                    Create Homepage
                  </Button>
                </div>
              ) : (
                <ListGroup variant="flush">
                  {allPages.map((pageId, index) => {
                    const sectionCount = pagesByContent[pageId] || 0;
                    const displayName = getPageDisplayName(pageId);
                    const description = getPageDescription(pageId);
                    const pagePath = `/page/${pageId}`;
                    
                    return (
                      <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center">
                            <FileText className="me-3 text-muted" />
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <strong>{displayName}</strong>
                                <Badge bg="secondary" pill>
                                  {sectionCount} section{sectionCount !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                              <div className="text-muted small mb-1">{description}</div>
                              <div className="text-primary small">
                                <code>{pagePath}</code>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button 
                            as={Link} 
                            to={pagePath} 
                            variant="outline-secondary" 
                            size="sm"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Eye className="me-1" />
                            View
                          </Button>
                          <Button 
                            as={Link} 
                            to={pagePath} 
                            variant="outline-primary" 
                            size="sm"
                          >
                            <PencilSquare className="me-1" />
                            Edit
                          </Button>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Help Information */}
      <Row className="mt-4">
        <Col>
          <Alert variant="info">
            <Database className="me-2" />
            <strong>Content Management System</strong>
            <br />
            This dashboard shows all pages with content in your CMS. Click "Edit" to modify page content using the built-in editor. 
            Changes are saved automatically and will be visible immediately on your website.
          </Alert>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminContentPage;
